import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppLoader from "../components/ui/AppLoader";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import DocumentCard from "../components/ui/DocumentCard";
import Icon from "../components/ui/Icon";
import Modal from "../components/ui/Modal";
import Tabs from "../components/ui/Tabs";
import { InputField } from "../components/ui/Field";
import api from "../api/axios-setup/axios";
import {
  deletePdfDocument,
  getPdfDocumentsByOrganization,
  PDF_DOCUMENTS_LIMIT,
  replacePdfDocument,
  uploadPdfDocuments,
} from "../api/pdf-documents-api";
import { getOrgId } from "../utils/authHelpers";
import { formatShortDate } from "../utils/format";

const DEFAULT_ORG_ID = "+61340541111";

function formatBytes(bytes) {
  const safeBytes = Number(bytes);
  if (!Number.isFinite(safeBytes) || safeBytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const power = Math.min(Math.floor(Math.log(safeBytes) / Math.log(1024)), units.length - 1);
  const value = safeBytes / 1024 ** power;
  return `${value.toFixed(value >= 10 || power === 0 ? 0 : 1)} ${units[power]}`;
}

function formatDate(value) {
  return formatShortDate(value).replace("N/A", "Unknown");
}

function getApiOrigin() {
  const configuredBaseUrl = String(api.defaults.baseURL || "").trim();
  if (!configuredBaseUrl) return "";
  return configuredBaseUrl.replace(/\/api\/v1\/?$/i, "");
}

function resolveDocumentUrl(rawUrl) {
  const normalized = String(rawUrl || "").trim();
  if (!normalized) return "";
  if (/^https?:\/\//i.test(normalized)) return normalized;
  const origin = getApiOrigin();
  if (!origin) return normalized;
  return `${origin}/${normalized.replace(/^\/+/, "")}`;
}

function buildPreviewUrl(url) {
  const value = String(url || "").trim();
  if (!value) return "";
  const hashSeparator = value.includes("#") ? "&" : "#";
  return `${value}${hashSeparator}zoom=page-width&toolbar=1&navpanes=0&scrollbar=1`;
}

function sanitizePdfText(value) {
  return String(value || "")
    .replace(/[^\x20-\x7E\n\r\t]/g, " ")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
}

function wrapText(text, maxCharsPerLine) {
  const lines = [];
  const paragraphs = String(text || "").split(/\r?\n/);

  paragraphs.forEach((paragraph) => {
    const clean = paragraph.trim();
    if (!clean) {
      lines.push("");
      return;
    }

    const words = clean.split(/\s+/);
    let currentLine = words[0] || "";

    for (let index = 1; index < words.length; index += 1) {
      const nextWord = words[index];
      if ((currentLine + " " + nextWord).length <= maxCharsPerLine) {
        currentLine += " " + nextWord;
      } else {
        lines.push(currentLine);
        currentLine = nextWord;
      }
    }

    lines.push(currentLine);
  });

  return lines;
}

function createPdfFileFromText(title, bodyText) {
  const pageWidth = 595;
  const pageHeight = 842;
  const margin = 52;
  const fontSize = 11;
  const lineHeight = 15;
  const contentWidth = pageWidth - margin * 2;
  const maxCharsPerLine = Math.max(50, Math.floor(contentWidth / (fontSize * 0.55)));
  const maxLinesPerPage = Math.floor((pageHeight - margin * 2) / lineHeight);

  const heading = String(title || "Untitled Document").trim();
  const wrapped = wrapText(bodyText, maxCharsPerLine);
  const lines = [heading, "", ...wrapped];
  const pages = [];

  for (let index = 0; index < lines.length; index += maxLinesPerPage) {
    pages.push(lines.slice(index, index + maxLinesPerPage));
  }

  if (!pages.length) pages.push([heading]);

  const objects = {};
  const pageIds = [];
  const totalObjects = 3 + pages.length * 2;

  objects[1] = "<< /Type /Catalog /Pages 2 0 R >>";
  objects[3] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>";

  pages.forEach((pageLines, pageIndex) => {
    const pageId = 4 + pageIndex * 2;
    const contentId = pageId + 1;
    pageIds.push(pageId);

    const textLines = pageLines
      .map((line) => `(${sanitizePdfText(line)}) Tj`)
      .join("\nT*\n");

    const stream = [
      "BT",
      `/F1 ${fontSize} Tf`,
      `${lineHeight} TL`,
      `${margin} ${pageHeight - margin} Td`,
      textLines,
      "ET",
    ].join("\n");

    const streamBytes = new TextEncoder().encode(stream).length;
    objects[contentId] = `<< /Length ${streamBytes} >>\nstream\n${stream}\nendstream`;
    objects[pageId] =
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] ` +
      `/Resources << /Font << /F1 3 0 R >> >> /Contents ${contentId} 0 R >>`;
  });

  objects[2] = `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`;

  let pdfContent = "%PDF-1.4\n";
  const offsets = [0];
  const encoder = new TextEncoder();

  for (let objectId = 1; objectId <= totalObjects; objectId += 1) {
    offsets[objectId] = encoder.encode(pdfContent).length;
    pdfContent += `${objectId} 0 obj\n${objects[objectId]}\nendobj\n`;
  }

  const xrefOffset = encoder.encode(pdfContent).length;
  pdfContent += `xref\n0 ${totalObjects + 1}\n0000000000 65535 f \n`;
  for (let objectId = 1; objectId <= totalObjects; objectId += 1) {
    pdfContent += `${String(offsets[objectId]).padStart(10, "0")} 00000 n \n`;
  }

  pdfContent += [
    "trailer",
    `<< /Size ${totalObjects + 1} /Root 1 0 R >>`,
    "startxref",
    `${xrefOffset}`,
    "%%EOF",
  ].join("\n");

  const blob = new Blob([pdfContent], { type: "application/pdf" });
  const normalizedTitle = heading
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const fileName = `${normalizedTitle || "document"}.pdf`;

  return new File([blob], fileName, { type: "application/pdf" });
}

function mapDocumentRecord(record) {
  const numericId = Number(record?.id);
  const viewUrl = resolveDocumentUrl(record?.download_url || record?.storage_url);
  const isoDate = record?.updated_at || record?.created_at;

  return {
    id: Number.isFinite(numericId)
      ? numericId
      : record?.doc_uuid || `${record?.document_name || "document"}-${record?.created_at || "0"}`,
    apiId: Number.isFinite(numericId) ? numericId : null,
    name: record?.document_name || "Untitled.pdf",
    sizeBytes: Number(record?.file_size_bytes || 0),
    size: formatBytes(record?.file_size_bytes),
    date: formatDate(isoDate),
    dateRaw: isoDate ? new Date(isoDate).getTime() : 0,
    orgId: record?.org_id || "",
    totalChunks: Number.isFinite(Number(record?.total_chunks))
      ? Number(record.total_chunks)
      : undefined,
    description: record?.file_path ? `Path: ${record.file_path}` : "",
    viewUrl,
    downloadUrl: viewUrl,
  };
}

function isPdfFile(file) {
  if (!file) return false;
  const fileName = String(file.name || "").toLowerCase();
  return file.type === "application/pdf" || fileName.endsWith(".pdf");
}

export function PdfDocumentsPage({ notify, setCurrentPage }) {
  const orgId = getOrgId() || DEFAULT_ORG_ID;
  const uploadInputRef = useRef(null);
  const replaceInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [creatingFromText, setCreatingFromText] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isDragActive, setDragActive] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewingDocument, setViewingDocument] = useState(null);
  const [activeComposerTab, setActiveComposerTab] = useState("upload");
  const [editorTitle, setEditorTitle] = useState("");
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [deletingById, setDeletingById] = useState({});
  const [replacingById, setReplacingById] = useState({});
  const editorRef = useRef(null);

  const fetchDocuments = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) setLoading(true);
        if (silent) setRefreshing(true);
        setError("");

        const response = await getPdfDocumentsByOrganization(orgId, 0, PDF_DOCUMENTS_LIMIT);
        const mapped = Array.isArray(response?.documents)
          ? response.documents.map(mapDocumentRecord)
          : [];
        mapped.sort((a, b) => (b.dateRaw || 0) - (a.dateRaw || 0));
        setDocuments(mapped);
      } catch (requestError) {
        const message =
          requestError?.response?.data?.detail ||
          requestError?.response?.data?.message ||
          requestError?.message ||
          "Failed to fetch PDF documents.";
        setError(message);
        setDocuments([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [orgId],
  );

  useEffect(() => {
    setCurrentPage("/business/pdfs");
  }, [setCurrentPage]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const filteredDocuments = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return documents;
    return documents.filter((document) => {
      return (
        document.name.toLowerCase().includes(query) ||
        String(document.orgId || "").toLowerCase().includes(query)
      );
    });
  }, [documents, searchTerm]);

  const stats = useMemo(() => {
    const totalBytes = documents.reduce((sum, document) => sum + (document.sizeBytes || 0), 0);
    const totalChunks = documents.reduce(
      (sum, document) => sum + (Number.isFinite(document.totalChunks) ? document.totalChunks : 0),
      0,
    );

    return {
      totalDocs: documents.length,
      totalSize: formatBytes(totalBytes),
      totalChunks,
    };
  }, [documents]);

  const runUpload = async (files) => {
    if (!files.length) return;

    if (!orgId) {
      notify?.({
        tone: "warning",
        title: "Missing organization ID",
        description: "Unable to upload without a valid organization ID.",
      });
      return;
    }

    setUploading(true);
    try {
      await uploadPdfDocuments(files, orgId);
      notify?.({
        tone: "success",
        title: "Upload complete",
        description: `${files.length} PDF file${files.length > 1 ? "s were" : " was"} uploaded.`,
      });
      await fetchDocuments({ silent: true });
    } catch (uploadError) {
      const message =
        uploadError?.response?.data?.detail ||
        uploadError?.response?.data?.message ||
        uploadError?.message ||
        "Upload failed. Please try again.";
      notify?.({
        tone: "danger",
        title: "Upload failed",
        description: message,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleFilesSelected = async (fileList) => {
    const incoming = Array.from(fileList || []);
    const validFiles = incoming.filter(isPdfFile);
    const rejectedCount = incoming.length - validFiles.length;

    if (rejectedCount > 0) {
      notify?.({
        tone: "warning",
        title: "Some files were skipped",
        description: "Only PDF files are allowed.",
      });
    }

    await runUpload(validFiles);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setDragActive(false);
    await handleFilesSelected(event.dataTransfer?.files);
  };

  const handleCreateFromEditor = async () => {
    const cleanTitle = editorTitle.trim();
    const cleanText = String(editorRef.current?.innerText || "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

    if (!cleanTitle) {
      notify?.({
        tone: "warning",
        title: "Title required",
        description: "Enter a title for the generated PDF.",
      });
      return;
    }

    if (!cleanText) {
      notify?.({
        tone: "warning",
        title: "Content required",
        description: "Type document content before generating the PDF.",
      });
      return;
    }

    setCreatingFromText(true);
    try {
      const generatedPdfFile = createPdfFileFromText(cleanTitle, cleanText);
      await uploadPdfDocuments([generatedPdfFile], orgId);
      notify?.({
        tone: "success",
        title: "Document created",
        description: "Your typed content was converted to PDF and uploaded.",
      });
      setEditorTitle("");
      if (editorRef.current) {
        editorRef.current.innerHTML = "<p><br></p>";
      }
      await fetchDocuments({ silent: true });
    } catch (generationError) {
      const message =
        generationError?.response?.data?.detail ||
        generationError?.response?.data?.message ||
        generationError?.message ||
        "Failed to create and upload PDF.";
      notify?.({
        tone: "danger",
        title: "Create failed",
        description: message,
      });
    } finally {
      setCreatingFromText(false);
    }
  };

  const runEditorCommand = (command, value = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, value);
  };

  const clearEditor = () => {
    if (!editorRef.current) return;
    editorRef.current.innerHTML = "<p><br></p>";
    editorRef.current.focus();
  };

  const handleDownload = (document) => {
    if (!document?.downloadUrl) {
      notify?.({
        tone: "warning",
        title: "Download unavailable",
        description: "No download URL was returned for this file.",
      });
      return;
    }

    const link = window.document.createElement("a");
    link.href = document.downloadUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download = document.name || "document.pdf";
    link.click();
  };

  const handleDelete = async (document) => {
    if (!document?.apiId) return;
    const confirmed = window.confirm(`Delete "${document.name}" permanently?`);
    if (!confirmed) return;

    setDeletingById((prev) => ({ ...prev, [document.id]: true }));
    try {
      await deletePdfDocument(document.apiId);
      setDocuments((prev) => prev.filter((entry) => entry.id !== document.id));
      notify?.({
        tone: "success",
        title: "Document deleted",
        description: `${document.name} was removed.`,
      });
    } catch (deleteError) {
      const message =
        deleteError?.response?.data?.detail ||
        deleteError?.response?.data?.message ||
        deleteError?.message ||
        "Failed to delete the document.";
      notify?.({
        tone: "danger",
        title: "Delete failed",
        description: message,
      });
    } finally {
      setDeletingById((prev) => ({ ...prev, [document.id]: false }));
    }
  };

  const handleReplaceStart = (document) => {
    setReplaceTarget(document);
    replaceInputRef.current?.click();
  };

  const handleReplaceFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !replaceTarget?.apiId) return;

    if (!isPdfFile(file)) {
      notify?.({
        tone: "warning",
        title: "Invalid file type",
        description: "Replacement file must be a PDF.",
      });
      return;
    }

    setReplacingById((prev) => ({ ...prev, [replaceTarget.id]: true }));
    try {
      await replacePdfDocument(replaceTarget.apiId, file);
      notify?.({
        tone: "success",
        title: "Document replaced",
        description: `${replaceTarget.name} was replaced successfully.`,
      });
      await fetchDocuments({ silent: true });
    } catch (replaceError) {
      const message =
        replaceError?.response?.data?.detail ||
        replaceError?.response?.data?.message ||
        replaceError?.message ||
        "Failed to replace document.";
      notify?.({
        tone: "danger",
        title: "Replace failed",
        description: message,
      });
    } finally {
      setReplacingById((prev) => ({ ...prev, [replaceTarget.id]: false }));
      setReplaceTarget(null);
    }
  };

  const isInitialLoading = loading && !documents.length && !error;

  if (isInitialLoading) {
    return (
      <div className="page pdf-documents-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">PDF Documents</h1>
            <p className="page-subtitle">
              Organization: <strong>{orgId}</strong>
            </p>
          </div>
        </div>
        <AppLoader
          className="section-gap"
          variant="page"
          label="Loading PDF documents"
          sublabel="Fetching your document library and training files..."
        />
      </div>
    );
  }

  return (
    <div className="page pdf-documents-page">
      <input
        ref={uploadInputRef}
        type="file"
        accept=".pdf,application/pdf"
        multiple
        hidden
        onChange={(event) => handleFilesSelected(event.target.files)}
      />
      <input
        ref={replaceInputRef}
        type="file"
        accept=".pdf,application/pdf"
        hidden
        onChange={handleReplaceFileSelected}
      />

      <div className="page-header">
        <div>
          <h1 className="page-title">PDF Documents</h1>
          <p className="page-subtitle">
            Organization: <strong>{orgId}</strong>
          </p>
        </div>
        <div className="page-actions">
          <Button
            variant="secondary"
            iconLeft={<Icon name="refresh" size={16} />}
            onClick={() => fetchDocuments({ silent: true })}
            loading={refreshing}
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            iconLeft={<Icon name="upload" size={16} />}
            onClick={() => uploadInputRef.current?.click()}
            loading={uploading}
          >
            Upload PDF
          </Button>
        </div>
      </div>

      <div className="pdf-summary-grid">
        <div className="pdf-summary-card">
          <span className="pdf-summary-label">Total Documents</span>
          <span className="pdf-summary-value">{stats.totalDocs}</span>
        </div>
        <div className="pdf-summary-card">
          <span className="pdf-summary-label">Total Storage</span>
          <span className="pdf-summary-value">{stats.totalSize}</span>
        </div>
        <div className="pdf-summary-card">
          <span className="pdf-summary-label">Total Chunks</span>
          <span className="pdf-summary-value">{stats.totalChunks}</span>
        </div>
      </div>

      <Card
        className="section-gap pdf-editor-card"
        title="PDF Workspace"
        subtitle="Build high-quality training data for your AI agent."
      >
        <div className="pdf-guidance-grid">
          <div className="pdf-guidance-card">
            <div className="pdf-guidance-title">Upload training documents</div>
            <div className="pdf-guidance-copy">
              Add files your agent should learn from: product details, FAQs, SOPs, policies,
              service menus, and support playbooks.
            </div>
          </div>
          <div className="pdf-guidance-card">
            <div className="pdf-guidance-title">Keep promotions current</div>
            <div className="pdf-guidance-copy">
              Upload new promotions, seasonal discounts, bundles, and campaign rules so the
              agent always responds with up-to-date offers.
            </div>
          </div>
          <div className="pdf-guidance-card">
            <div className="pdf-guidance-title">Use manual editor for quick updates</div>
            <div className="pdf-guidance-copy">
              Create instant documents for announcements, temporary pricing changes, and urgent
              business updates without leaving the dashboard.
            </div>
          </div>
        </div>

        <Tabs
          tabs={[
            { key: "upload", label: "Upload Training PDFs" },
            { key: "compose", label: "Create Training Document" },
          ]}
          activeKey={activeComposerTab}
          onChange={setActiveComposerTab}
        />

        {activeComposerTab === "upload" ? (
          <>
            <div
              className={`upload-zone ${isDragActive ? "is-drag-active" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => uploadInputRef.current?.click()}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  uploadInputRef.current?.click();
                }
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
            >
              <Icon name="upload" size={52} className="upload-icon" />
              <div className="upload-title">Upload agent training PDFs</div>
              <div className="upload-subtitle">
                Drag product info, promotions, pricing sheets, and policy docs here
              </div>
              <div className="upload-hint">or click to choose multiple PDF files</div>
            </div>

            {uploading ? (
              <AppLoader
                className="section-gap"
                variant="panel"
                label="Uploading training documents"
                sublabel="Your files are being processed and indexed for agent responses."
              />
            ) : null}
          </>
        ) : (
          <div className="pdf-editor-stack">
            <InputField
              label="Document title"
              placeholder="Customer Support Script"
              value={editorTitle}
              onChange={(event) => setEditorTitle(event.target.value)}
            />

            <div className="field">
              <span className="field-label">Document content</span>
              <div className="pdf-rich-editor-shell">
                <div className="pdf-rich-toolbar">
                  <button type="button" className="pdf-editor-tool" onClick={() => runEditorCommand("bold")}>
                    Bold
                  </button>
                  <button type="button" className="pdf-editor-tool" onClick={() => runEditorCommand("italic")}>
                    Italic
                  </button>
                  <button type="button" className="pdf-editor-tool" onClick={() => runEditorCommand("underline")}>
                    Underline
                  </button>
                  <button type="button" className="pdf-editor-tool" onClick={() => runEditorCommand("formatBlock", "H1")}>
                    H1
                  </button>
                  <button type="button" className="pdf-editor-tool" onClick={() => runEditorCommand("formatBlock", "H2")}>
                    H2
                  </button>
                  <button type="button" className="pdf-editor-tool" onClick={() => runEditorCommand("insertUnorderedList")}>
                    Bullet
                  </button>
                  <button type="button" className="pdf-editor-tool" onClick={() => runEditorCommand("insertOrderedList")}>
                    Numbered
                  </button>
                  <button type="button" className="pdf-editor-tool" onClick={() => runEditorCommand("formatBlock", "BLOCKQUOTE")}>
                    Quote
                  </button>
                  <button type="button" className="pdf-editor-tool is-danger" onClick={clearEditor}>
                    Clear
                  </button>
                </div>
                <div
                  ref={editorRef}
                  className="pdf-rich-editor"
                  contentEditable
                  suppressContentEditableWarning
                >
                  <p>Type your content here...</p>
                </div>
              </div>
              <div className="field-hint">
                Rich formatting helps writing, but PDF export keeps plain readable text.
              </div>
            </div>

            <div className="pdf-editor-actions">
              <Button
                variant="primary"
                iconLeft={<Icon name="save" size={16} />}
                onClick={handleCreateFromEditor}
                disabled={creatingFromText}
              >
                Convert and Upload
              </Button>
            </div>

            {creatingFromText ? (
              <AppLoader
                className="section-gap"
                variant="panel"
                label="Creating training document"
                sublabel="Generating PDF and syncing it to your agent knowledge base..."
              />
            ) : null}
          </div>
        )}
      </Card>

      <Card
        className="section-gap"
        title="Document Library"
        subtitle="Manage the documents your agent uses for answers and recommendations."
      >
        <div className="field">
          <span className="field-label">Search documents</span>
          <input
            className="input"
            placeholder="Search by file name or organization ID"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>

        {loading ? (
          <AppLoader
            className="section-gap"
            variant="panel"
            label="Loading documents"
            sublabel="Fetching latest PDF collection..."
          />
        ) : null}

        {error ? <div className="form-error section-gap">{error}</div> : null}

        {!loading && !error && !filteredDocuments.length ? (
          <div className="empty-state-box section-gap">
            <Icon name="pdf" size={28} />
            <div className="empty-state-title">No PDF documents found</div>
            <div className="empty-state-subtitle">
              Upload a PDF or generate one from the editor above.
            </div>
          </div>
        ) : null}

        {!loading && !error && filteredDocuments.length ? (
          <div className="doc-grid section-gap">
            {filteredDocuments.map((document) => (
              <DocumentCard
                key={document.id}
                document={document}
                onView={setViewingDocument}
                onDownload={handleDownload}
                onReplace={handleReplaceStart}
                onDelete={handleDelete}
                replacing={Boolean(replacingById[document.id])}
                deleting={Boolean(deletingById[document.id])}
              />
            ))}
          </div>
        ) : null}
      </Card>

      <Modal
        open={Boolean(viewingDocument)}
        className="pdf-viewer-modal"
        width="92vw"
        centered
        title={viewingDocument ? `Document Viewer - ${viewingDocument.name}` : "Document Viewer"}
        onClose={() => setViewingDocument(null)}
        actions={(
          <>
            <Button variant="secondary" onClick={() => setViewingDocument(null)}>
              Close
            </Button>
            <Button
              variant="primary"
              iconLeft={<Icon name="download" size={16} />}
              onClick={() => handleDownload(viewingDocument)}
            >
              Download
            </Button>
          </>
        )}
      >
        {viewingDocument?.viewUrl ? (
          <div className="pdf-viewer-shell">
            <div className="pdf-viewer-toolbar">
              <div>
                <div className="pdf-viewer-file">{viewingDocument.name}</div>
                <div className="pdf-viewer-meta">
                  {viewingDocument.size} | {viewingDocument.date}
                </div>
              </div>
              <a
                className="pdf-open-link"
                href={viewingDocument.viewUrl}
                target="_blank"
                rel="noreferrer"
              >
                Open In Browser
              </a>
            </div>
            <iframe
              title={viewingDocument.name}
              src={buildPreviewUrl(viewingDocument.viewUrl)}
              className="pdf-viewer-frame"
            />
          </div>
        ) : (
          <div className="form-error">No viewer URL available for this document.</div>
        )}
      </Modal>
    </div>
  );
}

export default PdfDocumentsPage;
